/**
 * ReportDndProvider — the dnd-kit context for the report editor canvas, replacing
 * react-grid-layout's native HTML5 drag/drop (Epic 37 canvas migration).
 *
 * Owns:
 *  - The DndContext + a PointerSensor (8px activation, matching the page-reorder
 *    DnD in the route so a click on a palette card never starts a drag).
 *  - The active palette drag identity (what the user picked up).
 *  - The DragOverlay — renders the SAME WidgetRenderer (via WidgetPreview) that the
 *    canvas renders, sized to the widget's true grid dimensions × current zoom.
 *    This is the WYSIWYG fix: the floating ghost ≡ the widget that will land,
 *    and because DragOverlay renders outside the document flow it is immune to the
 *    canvas's translate()/scale() transform (the RGL placeholder-offset bug).
 *
 * The actual drop placement is delegated to `onPaletteDrop`, supplied by the host
 * (the route), which adds the widget at a target cell. The canvas registers itself
 * as the droppable (Stage 2) and reports the hovered cell; here we just route the
 * drag lifecycle and paint the overlay.
 */

import { createContext, useContext, useState, useRef, useCallback, type ReactNode } from "react";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  pointerWithin,
  type DragStartEvent,
  type DragEndEvent,
  type Modifier,
} from "@dnd-kit/core";
import { getGridDimensions } from "@provenleap/types/report-rendering";
import { WidgetPreview } from "./WidgetPreview";
import { buildPreviewWidget } from "./widgetPreviewModel";
import { cellToPixelRect, type PaletteDragData, type GridCell } from "../../lib/reportDnd";

/** The canvas droppable id — the single drop target the palette drags into. */
export const CANVAS_DROPPABLE_ID = "report-canvas-droppable";

/**
 * Center the DragOverlay on the cursor. Without this, dnd-kit anchors the overlay
 * at the original draggable's top-left — so a big canvas-sized ghost dragged from
 * a small palette card trails far from the pointer (and from the snap placeholder,
 * which sits at the cursor). This recenters the ghost under the cursor so the two
 * agree. (Inline reimplementation of @dnd-kit/modifiers' snapCenterToCursor, to
 * avoid adding that dependency.)
 */
const snapCenterToCursor: Modifier = ({ activatorEvent, draggingNodeRect, transform }) => {
  if (!draggingNodeRect || !activatorEvent) return transform;
  const act = activatorEvent as PointerEvent;
  // Where on the dragged node the pointer started (relative to its top-left).
  const offsetX = act.clientX - draggingNodeRect.left;
  const offsetY = act.clientY - draggingNodeRect.top;
  // Shift so the pointer sits at the node's CENTER instead.
  return {
    ...transform,
    x: transform.x + offsetX - draggingNodeRect.width / 2,
    y: transform.y + offsetY - draggingNodeRect.height / 2,
  };
};

// ── Context: lets the canvas read the active palette drag (to size its live
// placeholder) and register its hover-cell getter (read on drop), without the
// route prop-drilling drag state through CanvasEditor → ReportCanvas. ───────────
interface ReportDndContextValue {
  /** The palette item currently being dragged, or null. */
  activeDrag: PaletteDragData | null;
  /** The canvas registers its hover-cell getter here; the provider reads it on drop. */
  registerHoverCellGetter: (getter: () => GridCell | null) => void;
  /** CanvasEditor registers its drop handler here (it owns handleWidgetAdd /
   *  handlePresetAdd with explicit-cell placement + auto-bind). */
  registerDropHandler: (handler: (data: PaletteDragData, cell: GridCell | null) => void) => void;
  /** CanvasGrid registers a widget-MOVE handler here. Called with the moved
   *  widget id + the screen-space drag delta; CanvasGrid applies the delta to the
   *  widget's cell (it owns the per-widget current position + grid metrics). */
  registerMoveHandler: (handler: (widgetId: string, delta: { x: number; y: number }) => void) => void;
}

const ReportDndCtx = createContext<ReportDndContextValue>({
  activeDrag: null,
  registerHoverCellGetter: () => {},
  registerDropHandler: () => {},
  registerMoveHandler: () => {},
});

/** Read the active palette drag + register the canvas hover-cell getter. */
export function useReportDnd(): ReportDndContextValue {
  return useContext(ReportDndCtx);
}

export interface ReportDndProviderProps {
  children: ReactNode;
  /** Active zoom scale (zoomLevel / 100) so the overlay matches the canvas size. */
  scale: number;
  format?: "document" | "slide-deck";
}

export function ReportDndProvider({
  children,
  scale,
  format = "document",
}: ReportDndProviderProps) {
  const [activeDrag, setActiveDrag] = useState<PaletteDragData | null>(null);

  // The canvas registers its hover-cell getter; we read it on drop. A ref avoids
  // re-rendering the provider as the hover cell changes during the drag.
  const hoverCellGetterRef = useRef<() => GridCell | null>(() => null);
  const registerHoverCellGetter = useCallback((getter: () => GridCell | null) => {
    hoverCellGetterRef.current = getter;
  }, []);

  // CanvasEditor registers its drop handler (owns explicit-cell add + auto-bind).
  const dropHandlerRef = useRef<(data: PaletteDragData, cell: GridCell | null) => void>(() => {});
  const registerDropHandler = useCallback(
    (handler: (data: PaletteDragData, cell: GridCell | null) => void) => {
      dropHandlerRef.current = handler;
    },
    [],
  );

  // CanvasGrid registers the widget-move handler (applies the delta to a cell).
  const moveHandlerRef = useRef<(widgetId: string, delta: { x: number; y: number }) => void>(() => {});
  const registerMoveHandler = useCallback(
    (handler: (widgetId: string, delta: { x: number; y: number }) => void) => {
      moveHandlerRef.current = handler;
    },
    [],
  );

  // 8px activation distance: a click (select) must not register as a drag — same
  // threshold as the page-reorder sensor and the old WidgetWrapper click-vs-drag.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
  );

  const handleDragStart = useCallback((e: DragStartEvent) => {
    const data = e.active.data.current as Partial<PaletteDragData> | undefined;
    if (data?.source === "palette") {
      setActiveDrag(data as PaletteDragData);
    }
  }, []);

  const handleDragEnd = useCallback(
    (e: DragEndEvent) => {
      const data = e.active.data.current as { source?: string } | undefined;
      if (data?.source === "palette") {
        if (e.over?.id === CANVAS_DROPPABLE_ID) {
          dropHandlerRef.current(e.active.data.current as PaletteDragData, hoverCellGetterRef.current());
        }
      } else if (data?.source === "widget") {
        // Existing-widget move: apply the screen-space delta to the widget's cell.
        moveHandlerRef.current(String(e.active.id), e.delta);
      }
      setActiveDrag(null);
    },
    [],
  );

  const handleDragCancel = useCallback(() => setActiveDrag(null), []);

  // Overlay sizing: the widget's true grid pixel size × current zoom, so the ghost
  // reads at exactly the on-canvas size. Reuses cellToPixelRect for the unscaled
  // dimensions, then scales the frame to match the zoomed canvas.
  const dims = getGridDimensions(format);
  const overlay = activeDrag
    ? (() => {
        const rect = cellToPixelRect(
          { row: 0, col: 0 },
          { colSpan: activeDrag.colSpan, rowSpan: activeDrag.rowSpan },
          { contentWidthPx: dims.contentWidthPx, rowHeightPx: dims.rowHeightPx, gapPx: dims.gapPx, paddingPx: 24 },
        );
        const built = buildPreviewWidget(
          {
            type: activeDrag.type,
            label: activeDrag.label,
            defaultColSpan: activeDrag.colSpan,
            defaultRowSpan: activeDrag.rowSpan,
          },
          activeDrag.platform ?? activeDrag.integrationId,
        );
        // Drag-carried config (content-block cards): the ghost renders the
        // configured content, so the overlay matches exactly what will land.
        const previewWidget = activeDrag.config
          ? { ...built, config: { ...built.config, ...activeDrag.config } }
          : built;
        return (
          <div
            style={{
              width: rect.width * scale,
              height: rect.height * scale,
              borderRadius: 12,
              overflow: "hidden",
              boxShadow: "0 10px 30px hsl(214 25% 20% / 0.22)",
              border: "0.5px solid var(--hairline)",
              background: "hsl(0 0% 100%)",
              opacity: 0.95,
            }}
            data-testid="palette-drag-overlay"
          >
            <WidgetPreview
              widget={previewWidget}
              scale={scale}
              format={format}
              frameWidth={rect.width}
              frameHeight={rect.height}
            />
          </div>
        );
      })()
    : null;

  return (
    <ReportDndCtx.Provider value={{ activeDrag, registerHoverCellGetter, registerDropHandler, registerMoveHandler }}>
      <DndContext
        sensors={sensors}
        collisionDetection={pointerWithin}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        {children}
        <DragOverlay dropAnimation={null} modifiers={[snapCenterToCursor]}>{overlay}</DragOverlay>
      </DndContext>
    </ReportDndCtx.Provider>
  );
}
